// Thin wrapper over SSEClient providing semantic subscriptions and normalized event envelope.
// Avoids duplicating connection / batching logic already implemented in sse-client.ts.

import { SSEClient, EventSourceHandler } from './sse-client';

// Base interface mirroring server `TopicComparableEvent` serialized form.
// All SSE events MUST implement these two discriminator fields plus their payload properties.
export interface TopicComparableEventBase {
  eventName: string; // server EventName override
  topic: string; // server Topic override (per-document / container / logical channel)
}

// Hub event simply wraps a strongly typed server event with receive metadata.
export interface HubEvent<
  T extends TopicComparableEventBase = TopicComparableEventBase,
> {
  receivedAt: number; // client epoch ms receipt time
  data: T; // raw server event object
}

export type HubEventHandler<
  T extends TopicComparableEventBase = TopicComparableEventBase,
> = (evt: HubEvent<T>) => void;

// Lightweight subscription record for auto-unsubscribe housekeeping
interface SubRecord {
  topic: string;
  handler: HubEventHandler<TopicComparableEventBase>;
}

// Topic helpers (central place so refactors are easy)
export const hubTopics = {
  /**
   * Build filecontainer topic string. Normalizes GUID casing + trims to avoid subtle mismatches
   * (server emits lowercase by default via Guid.ToString()).
   */
  fileContainer: (containerId: string) =>
    `filecontainer:${(containerId || '').trim().toLowerCase()}`,
  gdc: (gdcId: string) => `gdc:${(gdcId || '').trim()}`,
  /**
   * Build the topic carrying every site-assignment change made anywhere in one company.
   *
   * The name and shape are the server's (`SiteAssignmentTopicType.ByGroup`, SiteEvents.cs): it is
   * the third audience of one event whose other two topics are `site-assignment-updated:{siteId}`
   * and `user-assignments-updated:{userId}`, and the three read as a set. (The plain `group:{id}`
   * topic the people list once listened on is gone, #398 C5: nothing published on it, and the list
   * now re-asks on the user directory's user-updated event instead.)
   *
   * SUBSCRIBED TO BY A LIST OF PEOPLE, which is what neither of the other two can serve: a roster
   * does not know in advance which site or which person is about to change, and what it shows is
   * derived from every assignment in the company at once.
   */
  groupAssignments: (groupId: string) =>
    `group-assignments-updated:${(groupId || '').trim().toLowerCase()}`,
  /**
   * Build lock topic for a specific resource (by type and ID).
   * Server emits to this topic for lock events on a specific resource, heard by whoever may read
   * that record (#398 C10). There is no builder for the whole-type topic `locks/{type}`: the server
   * refuses it to everyone, since no read answers every record of a type.
   */
  lockResource: (resourceType: string, resourceId: string) =>
    `locks/${(resourceType || '').trim().toLowerCase()}/${(resourceId || '').trim().toLowerCase()}`,
};

// Known (initial) type discriminators to optionally narrow downstream logic
// Standardized fully-qualified filecontainer events (previous short forms removed)
export type FileContainerEventType =
  | 'filecontainer.file-added'
  | 'filecontainer.file-removed'
  | 'filecontainer.totals-updated';

// GDC event discriminators (future expansion). Dotted forms align exactly with server EventName overrides.
// NOTE: Update references rather than creating local aliases; server remains source of truth.
export type GdcEventType =
  'gdc.state-changed' | 'gdc.owners-updated' | 'gdc.metadata-updated';

// Lock event discriminators (align with server ResourceLockEvent.EventType values)
export type LockEventType =
  'lock-acquired' | 'lock-released' | 'lock-refreshed' | 'lock-expired';

export class SseHub {
  /** Backing singleton instance (explicit init only) */

  private static _instance: SseHub | undefined;
  /** Retrieve initialized hub instance or throw if not yet initialized */
  static get instance(): SseHub {
    if (!this._instance)
      throw new Error(
        'SseHub not initialized. Call initializeSseHub() after establishing the SSEClient in app bootstrap.'
      );
    return this._instance;
  }

  private readonly sse: SSEClient;
  private readonly subs = new Map<
    string,
    Set<HubEventHandler<TopicComparableEventBase>>
  >();
  private readonly allSubRecords: SubRecord[] = [];
  private initialized = false;
  private boundAllHandler?: EventSourceHandler;

  // Public constructor (treat as internal). Prefer initializeSseHub() for creation.
  constructor() {
    // Acquire existing SSE client (assumes it was already created/initialized elsewhere)
    // If not yet created, consumer should ensure SSEClient.getInstance is called with initializer earlier in app bootstrap.
    this.sse = SSEClient.getInstance();
  }

  /** Ensure we have a single global _all_ handler installed exactly once */
  private ensureInitialized() {
    if (this.initialized) return;
    this.boundAllHandler = (raw, _eventName) => this.routeRaw(raw);
    this.sse.addEventListener('', this.boundAllHandler);
    // Bound here, beside the event handler and under the same once-only guard, because both are
    // this hub's standing interest in its client. Safe to bind lazily: a refusal can only follow
    // a subscribe, and every subscribe through this hub runs this method first.
    this.sse.onTopicRefused((topic) => this.dropRefusedTopic(topic));
    this.initialized = true;
  }

  /**
   * The server refused a topic, so this hub stops holding it too.
   *
   * EVERY HANDLER FOR THE TOPIC GOES TOGETHER, which is the whole point rather than a shortcut:
   * the server refused the TOPIC, not one of the components listening to it, so there is no
   * subscriber for whom it is still valid. Leaving even one would keep the entry in `subs`, and
   * that entry is the first-subscriber gate in `subscribeTopic` - a later subscribe would be
   * swallowed here and never reach the client, which has already forgotten the topic. The two
   * layers would then disagree permanently and nothing would ever ask for the topic again.
   *
   * Dropping the `subs` entry is also what makes each handler's own unsubscribe closure a
   * harmless no-op afterwards: `unsubscribe` returns at its first guard when the set is gone, so
   * no remove is sent for a topic the connection never held and no count can go negative. The
   * records are purged here because that same early return means nothing else would ever clear
   * them.
   *
   * This drops the hub's record ONLY. It does not tell the client anything: the client is where
   * the refusal came from and has already swept its own stores.
   */
  private dropRefusedTopic(topic: string) {
    const trimmed = topic.trim();
    const set = this.subs.get(trimmed);
    if (!set) return;
    this.subs.delete(trimmed);
    for (let i = this.allSubRecords.length - 1; i >= 0; i--) {
      if (this.allSubRecords[i].topic === trimmed) {
        this.allSubRecords.splice(i, 1);
      }
    }
    const debug = (globalThis as { __SSE_DEBUG__?: boolean }).__SSE_DEBUG__;
    if (debug) {
      console.log('[sse-hub] refused topic dropped', {
        topic: trimmed,
        handlersDropped: set.size,
      });
    }
  }

  /** Subscribe to a raw topic */
  subscribeTopic<T extends TopicComparableEventBase = TopicComparableEventBase>(
    topic: string,
    handler: HubEventHandler<T>
  ): () => void {
    const trimmed = topic.trim();
    if (!trimmed) throw new Error('Topic required');
    this.ensureInitialized();

    const debug = (globalThis as { __SSE_DEBUG__?: boolean }).__SSE_DEBUG__;
    let set = this.subs.get(trimmed);
    if (!set) {
      set = new Set<HubEventHandler<TopicComparableEventBase>>();
      this.subs.set(trimmed, set);
      void this.sse.subscribeTopic(trimmed); // first logical subscriber triggers network subscribe
      if (debug) {
        console.log('[sse-hub] network subscribe', { topic: trimmed });
      }
    }
    set.add(handler as HubEventHandler<TopicComparableEventBase>);
    const rec: SubRecord = {
      topic: trimmed,
      handler: handler as HubEventHandler<TopicComparableEventBase>,
    };
    this.allSubRecords.push(rec);
    if (debug) {
      console.log('[sse-hub] handler subscribed', {
        topic: trimmed,
        totalHandlers: set.size,
      });
    }

    return () => this.unsubscribe(rec);
  }

  subscribeFileContainer<
    T extends TopicComparableEventBase = TopicComparableEventBase,
  >(containerId: string, handler: HubEventHandler<T>): () => void {
    return this.subscribeTopic(hubTopics.fileContainer(containerId), handler);
  }

  subscribeGdcDocument<
    T extends TopicComparableEventBase = TopicComparableEventBase,
  >(gdcId: string, handler: HubEventHandler<T>): () => void {
    return this.subscribeTopic(hubTopics.gdc(gdcId), handler);
  }

  /**
   * Subscribe to lock events for a specific resource.
   * Use this to monitor lock changes for a single resource.
   */
  subscribeLockResource<
    T extends TopicComparableEventBase = TopicComparableEventBase,
  >(
    resourceType: string,
    resourceId: string,
    handler: HubEventHandler<T>
  ): () => void {
    return this.subscribeTopic(
      hubTopics.lockResource(resourceType, resourceId),
      handler
    );
  }

  /** Unsubscribe one record */
  private unsubscribe(rec: SubRecord) {
    const set = this.subs.get(rec.topic);
    if (!set) return;
    set.delete(rec.handler);
    // Remove from master list
    const idx = this.allSubRecords.indexOf(rec);
    if (idx >= 0) this.allSubRecords.splice(idx, 1);
    if (set.size === 0) {
      this.subs.delete(rec.topic);
      void this.sse.unsubscribeTopic(rec.topic);
      const debug = (globalThis as { __SSE_DEBUG__?: boolean }).__SSE_DEBUG__;
      if (debug)
        console.log('[sse-hub] network unsubscribe (no handlers left)', {
          topic: rec.topic,
        });
    }
  }

  /** Normalize and fan-out raw server packet */
  private routeRaw(raw: unknown) {
    if (!raw || typeof raw !== 'object') return; // ignore malformed / keep-alive comments
    const obj = raw as TopicComparableEventBase;
    const topic = obj.topic;
    const name = obj.eventName;
    if (typeof topic !== 'string' || !topic) return; // must have topic
    if (typeof name !== 'string' || !name) return; // must have event name
    const normalizedTopic = topic.trim();
    const handlers = this.subs.get(normalizedTopic);
    if (!handlers?.size) return;
    const evt: HubEvent = { receivedAt: Date.now(), data: obj };
    handlers.forEach((h) => {
      try {
        h(evt);
      } catch (err) {
        console.warn('Hub handler error', err);
      }
    });
    const debug = (globalThis as { __SSE_DEBUG__?: boolean }).__SSE_DEBUG__;
    if (debug) {
      console.log('[sse-hub] routed event', {
        topic: normalizedTopic,
        eventName: name,
        handlers: handlers.size,
      });
    }
  }

  /**
   * Get a human-readable summary of current subscriptions for debugging.
   */
  getSubscriptionSummary(): {
    topics: Array<{ topic: string; handlerCount: number }>;
    totalHandlers: number;
  } {
    const topics: Array<{ topic: string; handlerCount: number }> = [];
    let totalHandlers = 0;

    for (const [topic, handlers] of this.subs.entries()) {
      topics.push({ topic, handlerCount: handlers.size });
      totalHandlers += handlers.size;
    }

    // Sort by topic name for readability
    topics.sort((a, b) => a.topic.localeCompare(b.topic));

    return { topics, totalHandlers };
  }
}

/**
 * Explicitly create (idempotent) the SSE Hub singleton.
 * Safe to call multiple times; first call wins.
 */
export function initializeSseHub(): SseHub {
  if (!(SseHub as unknown as { _instance?: SseHub })._instance) {
    (SseHub as unknown as { _instance?: SseHub })._instance = new SseHub();
  }
  return SseHub.instance;
}

/** Access the already-initialized hub; throws if not initialized. */
export function getSseHub(): SseHub {
  return SseHub.instance;
}

// Note: The previous eagerly-created exported singleton (sseHub) has been removed to
// enforce correct initialization order (SSE client -> hub). Update imports to use
// initializeSseHub() during application bootstrap and getSseHub() at call sites.

/**
 * Global debug function to show SSE subscription state.
 * Available in browser console as: showSseSub()
 */
function showSseSub(): void {
  console.group('🔔 SSE Subscription Summary');

  try {
    // Get SSE client data
    const sseClient = SSEClient.getInstance();
    const clientSummary = sseClient.getTopicSummary();

    console.group('📡 SSE Client');
    console.log(`Mode: ${clientSummary.mode}`);
    console.log(`Connected: ${clientSummary.isConnected ? '✅ Yes' : '❌ No'}`);
    console.log(`Session ID: ${sseClient.sessionId}`);
    console.groupEnd();

    console.group('📋 Server Topics (confirmed registered)');
    if (clientSummary.serverTopics.length === 0) {
      console.log('(none)');
    } else {
      clientSummary.serverTopics.forEach((t) => console.log(`  • ${t}`));
    }
    console.groupEnd();

    console.group('📝 Local Topic Refs (requested)');
    if (clientSummary.localTopics.length === 0) {
      console.log('(none)');
    } else {
      clientSummary.localTopics.forEach(({ topic, refCount }) =>
        console.log(`  • ${topic} (refCount: ${refCount})`)
      );
    }
    console.groupEnd();

    if (clientSummary.pendingBatch.length > 0) {
      console.group('⏳ Pending Batch Operations');
      clientSummary.pendingBatch.forEach(({ topic, action }) =>
        console.log(`  • ${action.toUpperCase()}: ${topic}`)
      );
      console.groupEnd();
    }
  } catch (e) {
    console.log('SSE Client not initialized');
  }

  try {
    // Get Hub data
    const hub = getSseHub();
    const hubSummary = hub.getSubscriptionSummary();

    console.group('🎯 Hub Event Handlers');
    console.log(`Total handlers: ${hubSummary.totalHandlers}`);
    if (hubSummary.topics.length === 0) {
      console.log('(none)');
    } else {
      hubSummary.topics.forEach(({ topic, handlerCount }) =>
        console.log(
          `  • ${topic} → ${handlerCount} handler${handlerCount !== 1 ? 's' : ''}`
        )
      );
    }
    console.groupEnd();
  } catch (e) {
    console.log('SSE Hub not initialized');
  }

  console.groupEnd();
}

// Register on globalThis for console access
(globalThis as { showSseSub?: typeof showSseSub }).showSseSub = showSseSub;
